import React, { useEffect, useRef, useState } from 'react'
import Icon from 'components/elements/Icon'
import { useRouter } from 'next/router'

import ToastService from 'services/toast.service'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import CardAutocomplete from 'components/formElements/CardAutocomplete'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_COLLECTION_V2_STATE } from 'redux/collectionV2/actions/types'

import { setCollectionQueryParams } from 'utils/queryParams/collection.queryParams'

import styles from './collectionFilter.module.scss'

type Props = {}

const CollectionFilter = ({}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const router = useRouter()

  const [syntaxQuery, setSyntaxQuery] = useState(`${router.query.syntaxQuery || ''}`)
  const [cardName, setCardName] = useState(`${router.query.cardName || ''}`)

  const [setCollectionV2State] = useActionless(SET_COLLECTION_V2_STATE)
  const filterType = useAppSelector(state => state.collectionV2.filterType)

  useEffect(() => {
    const normalizedSyntax = syntaxQuery.toLocaleLowerCase()

    if (normalizedSyntax.includes('order:') || normalizedSyntax.includes('direction:'))
      ToastService.create(
        'Order and/or direction was ignored. Syntax filtering only supports filtering on large data sets, use the other ordering tools on the site to determine order.',
        'Collection Page',
        'warning',
      )
  }, [])

  return (
    <form
      className={styles.filterForm}
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault()

        if (filterType === 'scryfall') return setCollectionQueryParams({ syntaxQuery, cardName: undefined })
        if (filterType === 'archidekt') return setCollectionQueryParams({ cardName, syntaxQuery: undefined })
      }}>
      <div className={styles.filter}>
        <label>
          Filter collection{' '}
          <span className={styles.extraLabel}>
            {filterType === 'scryfall' && (
              <>
                (syntax)
                <a href="https://scryfall.com/docs/syntax" rel="noopener noreferrer" target="_blank">
                  <Icon name="info circle" />
                </a>
              </>
            )}
            {filterType === 'archidekt' && '(card name)'}
          </span>
        </label>
        {filterType === 'scryfall' && (
          <input
            ref={inputRef}
            placeholder="c:red t:creature"
            type="text"
            autoCorrect="off"
            autoComplete="off"
            value={syntaxQuery}
            onChange={e => setSyntaxQuery(e.target.value)}
            onFocus={() => inputRef?.current?.select()}
          />
        )}
        {filterType === 'archidekt' && (
          <CardAutocomplete
            placeholder="Sol ring"
            value={cardName}
            onChange={(_e, { value }) => setCardName(value)}
            onSelect={cardName => setCollectionQueryParams({ cardName })}
            noIcon
          />
        )}
        <ArchidektDropdown
          options={[
            { label: 'Filter collection using...', type: 'header' },
            {
              label: 'Card name auto-complete',
              onClick: () => setCollectionV2State({ filterType: 'archidekt' }),
              icon: 'fast forward',
              className: styles.altInputOption,
            },
            {
              label: 'Syntax filter',
              onClick: () => setCollectionV2State({ filterType: 'scryfall' }),
              icon: 'code',
              className: styles.altInputOption,
            },
          ]}
          menuClassName={styles.filterMenu}
          triggerIcon="cog">
          <button className={styles.filterOptionsTrigger} type="button">
            <Icon name="cog" />
          </button>
        </ArchidektDropdown>
      </div>

      <button
        className={styles.moreFilterButton}
        type="button"
        onClick={() => setCollectionV2State({ filteringModalOpen: true })}>
        <Icon name="plus" /> Filters
      </button>
    </form>
  )
}

export default CollectionFilter
